import { ConnectAppCard } from "@app/components/actions/blocked/ConnectAppCard";
import { GranolaApiKeyRow } from "@app/components/actions/blocked/GranolaApiKeyRow";
import { usePendingSignIn } from "@app/components/actions/blocked/usePendingSignIn";
import {
  areCredentialOverridesValid,
  PersonalAuthCredentialOverrides,
} from "@app/components/oauth/PersonalAuthCredentialOverrides";
import { getAvatarFromIcon } from "@app/components/resources/resources_icons";
import { getMcpServerDisplayName } from "@app/lib/actions/mcp_helper";
import { canCurrentUserRespondToParentUserMessage } from "@app/lib/api/assistant/conversation/can_current_user_respond";
import type { MCPServerType } from "@app/lib/api/mcp";
import { useConnectViaComposio } from "@app/lib/swr/composio";
import {
  useCreatePersonalConnection,
  useMCPServer,
} from "@app/lib/swr/mcp_servers";
import type { OAuthProvider } from "@app/types/oauth/lib";
import { getOverridablePersonalAuthInputs } from "@app/types/oauth/lib";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import { useMemo, useRef, useState } from "react";

export type PersonalAuthResolutionOutcome = "completed" | "denied";

interface PersonalAuthenticationCardProps {
  triggeringUser: UserType | null;
  // The viewer looking at the card. Passed in rather than read from `AuthContext` because shared
  // frames render this card outside of any AuthProvider.
  currentUser: UserType;
  mcpServerId: string;
  owner: LightWorkspaceType;
  provider: OAuthProvider;
  scope?: string;
  isResolving: boolean;
  // Submits the resolution outcome; returns whether the submission succeeded.
  onResolve: (outcome: PersonalAuthResolutionOutcome) => Promise<boolean>;
}

export function PersonalAuthenticationCard({
  triggeringUser,
  currentUser,
  mcpServerId,
  owner,
  provider,
  scope,
  isResolving,
  onResolve,
}: PersonalAuthenticationCardProps) {
  const { server: mcpServer } = useMCPServer({
    owner,
    serverId: mcpServerId,
  });

  const { createPersonalConnection } = useCreatePersonalConnection(owner);

  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [overriddenCredentials, setCredentialOverrides] = useState<
    Record<string, string>
  >({});

  // Tracks whether the user abandoned (skipped) this action while a connection
  // attempt is still in flight, so the post-await "completed" branch does not
  // resolve an action that has already been denied.
  const cancelledRef = useRef<boolean>(false);

  const overridableInputs = getOverridablePersonalAuthInputs({ provider });

  const visual = mcpServer?.icon
    ? getAvatarFromIcon(mcpServer.icon, "md")
    : undefined;

  const serverDisplayName =
    mcpServer && mcpServer.name
      ? getMcpServerDisplayName(mcpServer)
      : undefined;

  const canCurrentUserRespond = useMemo(
    () =>
      canCurrentUserRespondToParentUserMessage({
        parentUserId: triggeringUser?.sId,
        currentUserId: currentUser.sId,
      }),
    [triggeringUser, currentUser.sId]
  );

  const {
    canConnectViaComposio,
    connectViaComposio,
    waitForComposioConnection,
  } = useConnectViaComposio({ owner, disabled: !canCurrentUserRespond });

  // The sign-in window records the grant itself, so after a reload mid
  // sign-in all that is left is to pick the turn back up.
  const pendingSignIn = usePendingSignIn({
    key: mcpServerId,
    provider,
    ready: canCurrentUserRespond,
    onFinalized: (connectionId) => {
      if (connectionId.isErr()) {
        setConnectionError(connectionId.error.message);
        return;
      }
      void onResolve("completed").then(setIsConnected);
    },
  });
  const isSigningIn = isConnecting || pendingSignIn.isResuming;

  const connect = async (
    mcpServer: MCPServerType
  ): Promise<{ success: boolean; error?: string }> => {
    if (!canConnectViaComposio(mcpServer.sId)) {
      pendingSignIn.start();
      const created = await createPersonalConnection({
        mcpServerId: mcpServer.sId,
        mcpServerDisplayName: getMcpServerDisplayName(mcpServer),
        authorization: mcpServer.authorization,
        provider,
        useCase: "personal_actions",
        scope,
        overriddenCredentials:
          Object.keys(overriddenCredentials).length > 0
            ? overriddenCredentials
            : undefined,
      });
      // A remount after a reload already picked this sign-in up.
      return pendingSignIn.claim() ? created : { success: false };
    }
    if (!(await connectViaComposio(mcpServer))) {
      return { success: false };
    }
    const connected = await waitForComposioConnection(
      mcpServer.sId,
      () => cancelledRef.current
    );
    return connected || cancelledRef.current
      ? { success: connected }
      : {
          success: false,
          error: "We didn't see the sign-in finish. Try connecting again.",
        };
  };

  const onConnectClick = async (mcpServer: MCPServerType) => {
    cancelledRef.current = false;
    setIsConnecting(true);
    setConnectionError(null);

    try {
      const result = await connect(mcpServer);

      if (!result.success) {
        setIsConnected(false);
        if (result.error) {
          setConnectionError(result.error);
        }
        return;
      }

      // The user skipped while the connection attempt was still in flight: the
      // action was already denied, so do not resolve it as completed.
      if (cancelledRef.current) {
        return;
      }

      const completed = await onResolve("completed");

      if (!completed) {
        setIsConnected(false);
        return;
      }

      setIsConnected(true);
    } finally {
      setIsConnecting(false);
    }
  };

  const onSkipClick = async () => {
    // Signal any in-flight connection attempt to abandon its completed branch.
    cancelledRef.current = true;
    setConnectionError(null);

    await onResolve("denied");
  };

  const name = serverDisplayName ?? "this tool";
  const message = !canCurrentUserRespond
    ? `${triggeringUser?.fullName ?? "The person who asked"} needs to connect ${name} before I can continue.`
    : isConnected
      ? `${name} is connected. Picking up from here.`
      : `I don't see your ${name} connected yet. Connect it and I'll pick up from here.`;

  return (
    <ConnectAppCard
      message={message}
      appName={name}
      visual={visual}
      detail={
        canCurrentUserRespond
          ? `Sign in with your ${name} account`
          : "Waiting on them to sign in"
      }
      isConnected={isConnected}
      error={connectionError}
      primary={
        canCurrentUserRespond && mcpServer
          ? {
              label: isSigningIn
                ? "Finish signing in…"
                : connectionError
                  ? "Try again"
                  : `Connect ${name}`,
              onClick: () => void onConnectClick(mcpServer),
              disabled:
                isSigningIn ||
                isResolving ||
                !areCredentialOverridesValid(
                  overridableInputs,
                  overriddenCredentials
                ),
            }
          : undefined
      }
      secondary={
        isSigningIn
          ? {
              label: "Start over",
              // A sign-in window closed before it finished never reports
              // back; this frees the button. A late success still resolves.
              onClick: () => {
                pendingSignIn.claim();
                setIsConnecting(false);
              },
            }
          : undefined
      }
      extra={
        canCurrentUserRespond &&
        mcpServer && (
          <GranolaApiKeyRow owner={owner} mcpServerId={mcpServer.sId} />
        )
      }
      dismiss={
        canCurrentUserRespond && mcpServer
          ? {
              label: "Not now",
              onClick: () => void onSkipClick(),
              // Not gated on `isConnecting`: the user must always be able to
              // abandon a connection attempt, even if it is (or appears) stuck.
              disabled: isResolving,
            }
          : undefined
      }
    >
      {canCurrentUserRespond && overridableInputs && mcpServer && (
        <PersonalAuthCredentialOverrides
          inputs={overridableInputs}
          values={overriddenCredentials}
          idPrefix={mcpServerId}
          onChange={(key, value) =>
            setCredentialOverrides((prev) => ({
              ...prev,
              [key]: value,
            }))
          }
        />
      )}
    </ConnectAppCard>
  );
}
